// src/lib/render/markdown-table.ts
//
// Parser for GitHub-flavored-markdown pipe tables embedded in question stems.
//
// Question stems (LLM-generated) frequently express two-way / data tables as
// markdown pipe tables:
//
//   | Category | Dog | No Dog | Total |
//   |----------|-----|--------|-------|
//   | Cat      | 12  | 18     | 30    |
//   | No Cat   | 25  | 65     | 90    |
//
// <StemRenderer> previously rendered only HTML <table>, so a markdown table
// leaked through as raw "| Dog | No Dog |" text (Probability two-way tables, and
// the audit found ~10 such stems). This pure parser splits a plain-text run into
// text + table segments so the renderer can draw real tables.
//
// Deliberately conservative to avoid false positives — most importantly it must
// NOT treat absolute-value notation ("|x| = 5", "|a| + |b|") as a table. A table
// requires a header row of pipe-delimited cells IMMEDIATELY followed by a
// delimiter row whose cells are only dashes/colons (---, :--, :-:). Absolute
// value never produces a dashes-only delimiter row, so it's safely ignored.

export type MarkdownTable = {
  headers: string[]
  rows: string[][]
}

export type MarkdownSegment =
  | { type: 'text'; text: string }
  | { type: 'table'; table: MarkdownTable }

/** A line is a candidate table row if it contains at least one pipe. */
function isPipeRow(line: string): boolean {
  return line.includes('|')
}

/**
 * Split a pipe row into trimmed cells, dropping the empty cells produced by
 * leading/trailing pipes (`| a | b |` → ['a','b'], `a | b` → ['a','b']).
 */
function splitCells(line: string): string[] {
  const cells = line.split('|').map((c) => c.trim())
  // Drop a leading empty (from a leading pipe) and a trailing empty (trailing pipe).
  if (cells.length && cells[0] === '') cells.shift()
  if (cells.length && cells[cells.length - 1] === '') cells.pop()
  return cells
}

/** A delimiter row's cells are each only optional colons around 1+ dashes. */
function isDelimiterRow(line: string): boolean {
  if (!isPipeRow(line)) return false
  const cells = splitCells(line)
  if (cells.length === 0) return false
  return cells.every((c) => /^:?-{1,}:?$/.test(c))
}

/**
 * Parse `text` into ordered text + table segments. Tables are recognized as a
 * header pipe-row, then a delimiter pipe-row, then zero or more body pipe-rows.
 * Everything else is preserved verbatim as text segments (including the original
 * newlines), so non-table content renders exactly as before.
 */
export function splitMarkdownTables(text: string): MarkdownSegment[] {
  const lines = text.split('\n')
  const segments: MarkdownSegment[] = []
  let textBuf: string[] = []

  const flushText = () => {
    if (textBuf.length) {
      segments.push({ type: 'text', text: textBuf.join('\n') })
      textBuf = []
    }
  }

  let i = 0
  while (i < lines.length) {
    const header = lines[i]
    const delim = lines[i + 1]
    // Table start: a pipe header immediately followed by a delimiter row.
    if (header !== undefined && delim !== undefined && isPipeRow(header) && isDelimiterRow(delim)) {
      const headers = splitCells(header)
      const colCount = headers.length
      const rows: string[][] = []
      let j = i + 2
      for (; j < lines.length; j++) {
        if (!isPipeRow(lines[j])) break
        const cells = splitCells(lines[j])
        // Normalize each body row to the header's column count.
        if (cells.length < colCount) {
          while (cells.length < colCount) cells.push('')
        } else if (cells.length > colCount) {
          cells.length = colCount
        }
        rows.push(cells)
      }
      flushText()
      segments.push({ type: 'table', table: { headers, rows } })
      i = j
      continue
    }
    textBuf.push(header)
    i++
  }
  flushText()
  return segments
}

/** True if `text` contains at least one markdown pipe table. */
export function hasMarkdownTable(text: string): boolean {
  return splitMarkdownTables(text).some((s) => s.type === 'table')
}

/**
 * True if `text` contains a markdown table in its BARE (unfenced) body — i.e. a
 * table that will actually render. A table trapped inside a ```chart / ```diagram
 * fenced block does NOT render as a table: StemRenderer routes fenced blocks to
 * the chart/diagram parser, which can't parse a pipe table and shows a
 * "could not be parsed" error. Generators/validators that want a real table must
 * use this, not hasMarkdownTable (which is fooled by a fenced table).
 */
export function hasBareMarkdownTable(text: string): boolean {
  const unfenced = (text || '').replace(/```(?:chart|diagram)\n[\s\S]*?```/g, '')
  return hasMarkdownTable(unfenced)
}
